import { Injectable, Inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { AppState } from "../models/app.state";
import { ENV_VARS } from "../environment-variables/environment-variables.module";
import { HttpClient, HttpParams, HttpHeaders } from "@angular/common/http";
import {
  IDiaprofilUser,
  UserTypes,
  DIAPROFIL_QUESTIONNAIRE_UUID,
  IDiaprofilHcp,
  IUsageStatistics,
  proTaskConsultationType,
  proTaskConsultationTypeOptions,
  ITypewriterResult,
  IDiaprofilServiceMessage,
  IDiaprofilNews,
  IClinicalSuiteIncomingParams,
  InitEhrResult
} from "../models/types";
import { UserState } from "../store/reducers/user.reducer";
import * as PatientActions from "../store/actions/patient.actions";
import * as HcpUserActions from "../store/actions/hcp-user.actions";
import { FhirService } from "./fhir.service";
import { forkJoin } from "rxjs/observable/forkJoin";
import {
  DiaprofilPatient,
  IDiaprofilPatient,
  IProTask
} from "../models/diaprofil-patient";
import { QuestionnaireResponseService } from "@zitelab/ionic-fhir-questionnaires";
import { TranslateService } from "@ngx-translate/core";
import { DashboardService } from "./dashboard.service";
import { of } from "rxjs/observable/of";
import { ConfigState } from "../store/reducers/config.reducer";
import { AlertController } from "ionic-angular/components/alert/alert-controller";
import { DiaProfilService } from "./diaprofil.service";
import { codingMatch } from "../utils/coding-match";
import { Observable } from "rxjs/Observable";
import { switchMap } from "rxjs/operators/switchMap";
import { map } from "rxjs/operators/map";
import * as UserActions from "../store/actions/user.actions";
import * as ConfigActions from "../store/actions/config.actions";
import * as NewsActions from "../store/actions/news.actions";
import * as ServiceMessageActions from "../store/actions/service-message.actions";
import { interceptorSkipHeader } from "./auth.interceptor";
import { tap, catchError } from "rxjs/operators";
import { AuthService } from "./auth.service";

export interface ICprLookupResult {
  success: boolean;
  error_code: string;
  error_msg: string;
  data?: {
    fullName: string;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    postalCode: string;
  };
}

@Injectable()
export class AdminService {
  userState: UserState;
  orgConfig: ConfigState;
  hcpUsers: IDiaprofilHcp[] = [];
  patients: DiaprofilPatient[] = [];
  news: IDiaprofilNews[] = [];
  serviceMessages: IDiaprofilServiceMessage[] = [];
  baseUrl: URL;
  endpoints: { [key: string]: string };
  availableConsultationTypes: {
    id: proTaskConsultationType;
    i18n: string;
  }[] = [];
  constructor(
    public http: HttpClient,
    public store: Store<AppState>,
    public fhir: FhirService,
    public patient: DiaprofilPatient,
    public questionnaireResponseService: QuestionnaireResponseService,
    public dashboard: DashboardService,
    public translateService: TranslateService,
    public diaprofil: DiaProfilService,
    public auth: AuthService,
    public alertCtrl: AlertController,
    @Inject(ENV_VARS) public envVars
  ) {
    this.baseUrl = envVars.API_URL;
    this.endpoints = {
      users: "@fhir-user",
      ehrMode: "@ehr-init",
      statistics: "@statistics",
      notificationLog: "@notification-log",
      cprLookup: "@diaprofil-cpr-lookup" // '@cpr-lookup'
    };
    this.store
      .select(state => state.config)
      .subscribe(config => {
        this.orgConfig = config;
        this.availableConsultationTypes = proTaskConsultationTypeOptions.filter(
          elem => {
            if (
              this.orgConfig.hasOwnProperty(
                "ffProTaskConsultationTypeExtOptions"
              ) &&
              this.orgConfig.ffProTaskConsultationTypeExtOptions !== null
            ) {
              return this.orgConfig.ffProTaskConsultationTypeExtOptions.includes(
                elem.id
              );
            } else {
              return false;
            }
          }
        );
      });
    this.store
      .select(state => state.user)
      .subscribe(user => {
        this.userState = user;
      });
    this.store
      .select(state => state.hcpUsers)
      .subscribe(hcpUsers => {
        this.hcpUsers = hcpUsers;
      });
    this.store
      .select(state => state.patients)
      .subscribe(patients => {
        this.patients = patients.map(
          patient =>
            new DiaprofilPatient(
              patient,
              translateService,
              questionnaireResponseService,
              dashboard
            )
        );
      });
    this.store
      .select(state => state.news)
      .subscribe(news => {
        this.news = news
          .filter(item => item.display !== "No")
          .sort((a, b) => (a.date <= b.date ? 1 : -1));
      });
    this.store
      .select(state => state.serviceMessages)
      .subscribe(serviceMessages => {
        this.serviceMessages = serviceMessages.sort((a, b) =>
          a.date <= b.date ? 1 : -1
        );
      });
  }

  getOrgStatistics(): Observable<IUsageStatistics> {
    const options = {
      params: new HttpParams().set(
        "organization_uuid",
        this.userState.organizationUuid
      )
    };
    return this.http.get<IUsageStatistics>(
      `${this.baseUrl}${this.endpoints.statistics}`,
      options
    );
  }

  addPatientsToStore = (users: IDiaprofilUser[]) => {
    users.map(user => {
      this.fhir
        .getQuery({
          resourceType: "Task",
          queryParams: {
            code: "|parentTask",
            intent: "DIAPROFIL",
            owner: user.fhir_id,
            _sort: "-authored-on",
            "part-of:missing": "true"
          }
        })
        .pipe(
          switchMap(taskBundle => {
            const parentTasks: fhir.Task[] =
              taskBundle.total > 0
                ? taskBundle.entry.map(entry => entry.resource as fhir.Task)
                : [];
            return this.addPatientHandler(user, parentTasks);
          })
        )
        .subscribe();
    });
  };

  addPatientHandler = (
    user: IDiaprofilUser = null,
    parentTasks: fhir.Task[],
    proTasks: {
      [key: string]: IProTask;
    } = {}
  ): Observable<IDiaprofilPatient> => {
    const queries = parentTasks.map(task => {
      proTasks[task.groupIdentifier.value] = {
        task: task,
        procedureRequest: null,
        response: null
      };
      return this.fhir
        .getQuery({
          resourceType: "ProcedureRequest",
          queryParams: {
            definition: `Questionnaire/${DIAPROFIL_QUESTIONNAIRE_UUID}`,
            requisition: task.groupIdentifier.value
          }
        })
        .pipe(
          switchMap(requestBundle => {
            const request =
              requestBundle.total > 0
                ? (requestBundle.entry[0].resource as fhir.ProcedureRequest)
                : null;
            proTasks[task.groupIdentifier.value].procedureRequest = request;
            return this.fhir
              .getQuery({
                resourceType: "QuestionnaireResponse",
                queryParams: {
                  author: user.fhir_id,
                  _sort: "authored",
                  status: "completed,amended",
                  "based-on": `ProcedureRequest/${
                    request !== null ? request.id : "NO_REQUEST_FOUND" // sending mock request
                  }`,
                  questionnaire: `Questionnaire/${DIAPROFIL_QUESTIONNAIRE_UUID}`,
                  _count: 1
                }
              })
              .pipe(
                map(responseBundle => {
                  const response =
                    responseBundle.total > 0
                      ? (responseBundle.entry[0]
                          .resource as fhir.QuestionnaireResponse)
                      : null;
                  proTasks[task.groupIdentifier.value].response = response;
                  return proTasks[task.groupIdentifier.value];
                })
              );
          })
        );
    });
    const obs$ = queries.length > 0 ? forkJoin(queries) : of([]); // We need to ensure that some observable is returned even if no pro tasks
    return obs$.pipe(
      map(proTasks => {
        const patientResource = user.user_resource as fhir.Patient;
        const patientObj: IDiaprofilPatient = {
          fhirId: user.fhir_id,
          userName: user.username,
          fhirResource: patientResource,
          socialSecurityNumber: user.social_security_number,
          fullName: user.fullname,
          homePath: user.home_path,
          language: user.language,
          mobileNumber: user.mobile_number,
          organizationUid: user.organization_uid,
          bloodSugarMedicineTypes: user.bloodSugarMedicineTypes,
          consentToSms: user.consentToSms,
          diagnosis: user.diagnosis,
          diagnosisYear: user.diagnosisYear,
          evalConsent: user.evalConsent,
          evalConsentResponseUuid: user.evalConsentResponseUuid,
          municipality: user.municipality,
          isDeactivated: user.isDeactivated,
          deactivationReason: user.deactivationReason,
          deactivationTimestamp: user.deactivationTimestamp,
          evalStudyParticipantId: user.evalStudyParticipantId,
          proTasks: proTasks,
          consultations: [] // consultationsResults
        };
        this.store.dispatch(new PatientActions.ReplaceAction(patientObj));
        return patientObj;
      })
    );
  };

  isFeatureEnabled(
    feature:
      | "ffHcpUsersPage"
      | "ffStatisticsPage"
      | "ffDiagnosisTimepointInDashboard"
      | "ffHcpProPatch"
      | "ffSoftRequiredMobileNumber"
      | "ffSsnRegistryIntegration"
      | "ffCurrentConsultationsPage"
      | "ffKramBtnInDashboard"
  ) {
    const featureValue = this.orgConfig[feature];
    if (featureValue === "ENABLED") {
      return true;
    } else if (featureValue === "ENABLED_FOR_SUPER_USERS") {
      return this.userState.isSuperUser;
    } else {
      return false;
    }
  }

  getPatients(organizationUuid) {
    this.getUsers({
      userType: "Patient",
      organizationUuid: organizationUuid,
      extraAction: this.addPatientsToStore
    });
  }

  getHcps(organizationUuid) {
    const addHcpsToStore = (users: IDiaprofilUser[]) => {
      users.forEach(user => {
        const practitionerResource = user.user_resource as fhir.Practitioner;
        const hcpObject: IDiaprofilHcp = {
          userRole: "Practitioner",
          fhirId: user.fhir_id,
          userName: user.username,
          hcpInitials: user.hcpInitials,
          csUniqueId: user.cs_unique_id,
          isDeactivated: user.isDeactivated,
          deactivationReason: user.deactivationReason,
          deactivationTimestamp: user.deactivationTimestamp,
          evalStudyParticipantId: user.evalStudyParticipantId,
          staffType: user.staffType,
          fhirResource: practitionerResource,
          fullName: user.fullname,
          homePath: user.home_path,
          mobileNumber: user.cell_phone_number,
          lastLoginTime: user.last_login_time,
          email: user.email,
          organizationUid: user.organization_uid,
          twoFactorEnabled: user.twofactor_enabled,
          twoFactorMethods: user.twofactor_messaging_methods
        };
        this.store.dispatch(new HcpUserActions.ReplaceAction(hcpObject));
      });
    };
    this.getUsers({
      userType: "Practitioner",
      organizationUuid: organizationUuid,
      extraAction: addHcpsToStore
    });
  }

  getPatientUser(fhirId): Observable<IDiaprofilUser> {
    const options = {
      params: new HttpParams({
        fromObject: { fhir_id: fhirId }
      })
    };
    return this.http
      .get<IDiaprofilUser[]>(`${this.baseUrl}${this.endpoints.users}`, options)
      .pipe(
        map(result => {
          if (Array.isArray(result)) {
            return result[0];
          }
          return result;
        })
      );
  }

  getUsers(props: {
    userType: UserTypes;
    organizationUuid: string;
    fhirId?: string;
    extraAction?: (users: IDiaprofilUser[]) => void;
  }) {
    const params = {
      user_type: props.userType,
      "organization_uid:": props.organizationUuid
    };
    if (props.fhirId !== undefined) {
      params["fhir_id"] = props.fhirId;
    }
    const options = {
      params: new HttpParams({
        fromObject: params
      })
    };
    return this.http
      .get<IDiaprofilUser[]>(`${this.baseUrl}${this.endpoints.users}`, options)
      .subscribe(
        users => {
          if (props.hasOwnProperty("extraAction")) {
            props.extraAction(users);
          }
        },
        // Errors will call this callback instead:
        err => {
          console.log(err);
        }
      );
  }

  fetchServiceMessages(): void {
    this.http
      .get<ITypewriterResult[]>(
        "https://api.typewriter.cloud/ZiteLab/diaprofil/types/service-messages",
        { headers: new HttpHeaders().set(interceptorSkipHeader, "") }
      )
      .subscribe(result => {
        if (!Array.isArray(result)) {
          return;
        }
        result.map(raw => {
          const fields = raw.fields.reduce((acc, cur) => {
            acc[cur.slug] = cur.value;
            return acc;
          }, {});
          const msg = {
            id: raw.slug,
            ...fields
          } as IDiaprofilServiceMessage;
          this.store.dispatch(new ServiceMessageActions.ReplaceAction(msg));
        });
      });
  }

  fetchNews(): void {
    this.http
      .get<ITypewriterResult[]>(
        "https://api.typewriter.cloud/ZiteLab/diaprofil/types/news",
        { headers: new HttpHeaders().set(interceptorSkipHeader, "") }
      )
      .subscribe(result => {
        if (!Array.isArray(result)) {
          return;
        }
        result.map(raw => {
          const fields = raw.fields.reduce((acc, cur) => {
            acc[cur.slug] = cur.value;
            return acc;
          }, {});
          const news = {
            id: raw.slug,
            ...fields
          } as IDiaprofilNews;
          this.store.dispatch(new NewsActions.ReplaceAction(news));
        });
      });
  }

  triggerEhrMode(
    params: IClinicalSuiteIncomingParams
  ): Observable<InitEhrResult> {
    return this.http
      .post<InitEhrResult>(`${this.baseUrl}${this.endpoints.ehrMode}`, params)
      .pipe(
        tap(result => console.log(result, "triggerEhrMode")),
        map(result => {
          if (result.multiple) {
            return { ...result, hasError: false, multiple: true };
          }

          if (result.cs_user === null) {
            return { ...result, hasError: true, errorMsg: "USER_NOT_FOUND" };
          } else {
            this.store.dispatch(
              new UserActions.SetJWTAction({
                jwt: result.cs_user.jwt_token,
                userType: "Practitioner"
              })
            );

            this.auth
              .setOrganizationConfig(result.cs_user.organization_uid)
              .subscribe();

            this.store.dispatch(
              new ConfigActions.UpdateAction({ ehrMode: true })
            );

            return { ...result, hasError: false, errorMsg: null };
          }
        }),
        catchError(error => {
          console.log(error);
          return of({ hasError: true, errorMsg: "UNAUTHORIZED" });
        })
      );
  }

  public cprLookup(
    cpr: string,
    lookupReason: string
  ): Observable<ICprLookupResult> {
    const options = {
      params: new HttpParams()
        .set("cpr", cpr)
        .set("lookup_reason", lookupReason)
    };
    return this.http.get<ICprLookupResult>(
      `${this.baseUrl}${this.endpoints.cprLookup}`,
      options
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/services/admin.service.ts